/**
 * What a drag or a rename in the Documents tree would write (RFC 0338 3c).
 *
 * Pure over paths, so the rules that decide whether a folder lights up under
 * the pointer are testable without React or a repo. They mirror the refusals
 * in core's `moveDocument` and `moveFolder`: a drop the core would refuse is a
 * drop the sidebar never offers, so a refused write is never something a
 * person sees happen.
 *
 * A folder here is a path prefix, `''` for the top of a project's documents.
 */

const segments = (path: string): string[] =>
  path.split('/').filter((s) => s.length > 0)

const join = (folder: string, name: string): string =>
  folder ? `${folder}/${name}` : name

/** The folder a path sits in, `''` at the top. */
export function parentOf(path: string): string {
  return segments(path).slice(0, -1).join('/')
}

/** The last segment of a path: a file's name, or a folder's. */
export function baseOf(path: string): string {
  return segments(path).at(-1) ?? ''
}

/** Where a document lands when dropped on `folder`. */
export function documentTarget(docPath: string, folder: string): string {
  return join(folder, baseOf(docPath))
}

/**
 * Where a document dropped on `folder` lands, made free when its name is
 * taken there: `0278-plan.md` becomes `0278-plan-2.md`. A path is a key the
 * tree shows as a row; the title is what a person reads, and it does not
 * change. Refusing the drop instead left a document that would not go into
 * `rfcs` with nothing on screen saying why.
 */
export function freeDocumentTarget(
  docPath: string,
  folder: string,
  taken: ReadonlySet<string>,
): string {
  const target = documentTarget(docPath, folder)
  if (!taken.has(target)) return target
  const name = baseOf(docPath)
  const dot = name.lastIndexOf('.')
  const stem = dot > 0 ? name.slice(0, dot) : name
  const ext = dot > 0 ? name.slice(dot) : ''
  for (let n = 2; ; n++) {
    const next = join(folder, `${stem}-${n}${ext}`)
    if (!taken.has(next)) return next
  }
}

/** `to`, or `to-2`, `to-3`... until no path in `taken` is it or inside it:
 *  where a folder lands when a person chooses rename on a clash. */
export function freeFolderTarget(
  to: string,
  taken: ReadonlySet<string>,
): string {
  const used = (path: string) =>
    [...taken].some((p) => p === path || p.startsWith(`${path}/`))
  if (!used(to)) return to
  for (let n = 2; ; n++) if (!used(`${to}-${n}`)) return `${to}-${n}`
}

/** A document may be dropped on any folder but the one it is already in. */
export function canDropDocument(docPath: string, folder: string): boolean {
  return parentOf(docPath) !== folder
}

/** Where a folder lands when dropped on `into`. */
export function folderTarget(from: string, into: string): string {
  return join(into, baseOf(from))
}

/** Mirror the source placement refusals in core before lighting a drop target
 * or asking the daemon to mount a newly chosen directory. An ordinary parent
 * folder may contain documents; the source's own child path may not overlap a
 * document, another source, or a nested project. */
export function canPlaceSourceAt(
  sourceId: string,
  target: string,
  documentPaths: Iterable<string>,
  sources: Iterable<{ id: string; folder?: string }>,
  projectMounts: Iterable<string>,
): boolean {
  return (
    sourcePlacementClash(
      sourceId,
      target,
      documentPaths,
      sources,
      projectMounts,
    ) === null
  )
}

/**
 * What stands in the way of a source at `target`, or null when nothing does
 * (RFC 0357 3i). `taken`: something sits at `target` or under it, so landing
 * beside it under a free name (`app-2`) is an answer. `inside`: `target` is
 * under a document, another source or a nested project, which no rename of
 * the source fixes, so the drop is refused.
 */
export function sourcePlacementClash(
  sourceId: string,
  target: string,
  documentPaths: Iterable<string>,
  sources: Iterable<{ id: string; folder?: string }>,
  projectMounts: Iterable<string>,
): 'taken' | 'inside' | null {
  if (!target || target.startsWith(':source/')) return 'inside'
  const held: string[] = [
    ...documentPaths,
    ...[...sources]
      .filter((source) => source.id !== sourceId && !!source.folder)
      .map((source) => source.folder as string),
    ...projectMounts,
  ]
  if (held.some((path) => target.startsWith(`${path}/`))) return 'inside'
  if (held.some((path) => path === target || path.startsWith(`${target}/`)))
    return 'taken'
  return null
}

/**
 * A folder may move from `from` to `to` when that changes something, does not
 * put it inside itself, and no document it carries lands on a path a document
 * outside it already has. All or nothing, as `moveFolder` is.
 */
export function canMoveFolder(
  from: string,
  to: string,
  paths: Iterable<string>,
): boolean {
  if (!from || !to || from === to) return false
  if (to.startsWith(`${from}/`)) return false
  const prefix = `${from}/`
  const moving: string[] = []
  const staying = new Set<string>()
  for (const p of paths) {
    if (p === from || p.startsWith(prefix)) moving.push(p)
    else staying.add(p)
  }
  if (moving.length === 0) return false
  return moving.every(
    (p) => !staying.has(p === from ? to : `${to}/${p.slice(prefix.length)}`),
  )
}

/**
 * A folder may move to ANOTHER project (RFC 0338 S5) when it holds something
 * and nothing it carries lands on a path the other project already has.
 * `fromPaths` are the old project's paths, `taken` the new project's.
 */
export function canMoveFolderAcross(
  from: string,
  to: string,
  fromPaths: Iterable<string>,
  taken: ReadonlySet<string>,
): boolean {
  if (!from || !to) return false
  const prefix = `${from}/`
  let any = false
  for (const p of fromPaths) {
    if (p !== from && !p.startsWith(prefix)) continue
    any = true
    if (taken.has(p === from ? to : `${to}/${p.slice(prefix.length)}`))
      return false
  }
  return any
}

/**
 * The folder a rename to `name` means: the same parent, a new last segment.
 * Null for a name that is empty or has a slash, since a rename that moves the
 * folder somewhere else is a drag, not a rename.
 */
export function renamedFolder(from: string, name: string): string | null {
  const next = name.trim()
  if (!next || next.includes('/') || next === '.' || next === '..') return null
  return join(parentOf(from), next)
}

/**
 * Carry open-state keys across a folder move. The tree keys a folder by its
 * path, so without this a renamed folder would reopen in its default state,
 * closed, as if it were new.
 */
export function carryFolderKeys(
  keys: ReadonlySet<string>,
  from: string,
  to: string,
): Set<string> {
  const out = new Set<string>()
  for (const k of keys) {
    if (k === from) out.add(to)
    else if (k.startsWith(`${from}/`)) out.add(`${to}${k.slice(from.length)}`)
    else out.add(k)
  }
  return out
}
